import React, { useState, useContext, useEffect, memo, useRef, useMemo } from "react";
import { AuthContext } from "../context/AuthContext";
import { ProfileContext } from "../context/ProfileContext";
import { WerkerJobContext } from "../context/JobContextWerker";
import { SchedJobContext } from "../context/JobContextScheduler";
import { WerkerSchedulerHeader } from "../components/WerkHeader";
import { LoadingScreen } from "../components/LoadingScreen";
import WerkerOnboarding from "../components/WerkerOnboarding";
import SchedulerOnboarding from "../components/SchedulerOnboarding";
import { fx_FB_UpdateOnboardingSeen } from "../functions/Fx_FirebaseUpdateFunctions";
import {
  IonContent,
  IonPage,
  IonCol,
  IonRow,
  IonLabel,
  IonSegment,
  IonSegmentButton,
  IonIcon,
  IonFab,
  IonFabButton,
  IonBadge,
  useIonAlert,
  IonSearchbar,
  IonFabList,
  IonToast,
  IonButton,
  IonSelect,
  IonSelectOption,
  IonChip,
  IonPopover,
  IonList,
  IonItem,
  IonCheckbox,
  useIonToast,
} from "@ionic/react";
import { useHistory, Redirect } from "react-router-dom";
import {
  HEADERTEXT_TYPES,
  PUSH_NOTIFICATION_TYPES,
  WORKFLOW_WERKER_TYPES,
  WORKFLOW_SCHEDULER_TYPES,
  WERK_SEGMENT_SELECTOR,
  FB_COLLECTION_PATH,
} from "../functions/postActionTypes";
import "../theme/AppStyles.css";
import {
  add,
  addOutline,
  calendarOutline,
  cashOutline,
  filterOutline,
  listOutline,
  megaphoneOutline,
  shareSocialOutline,
  swapVerticalOutline,
} from "ionicons/icons";
import { db } from "../firebase";
import { query, where, collectionGroup, doc, updateDoc, getDocs, setDoc } from "@firebase/firestore";
import { ActionPerformed, PushNotifications, PushNotificationSchema, Token } from "@capacitor/push-notifications";
import TabNavigator from "../components/TabNavigator";
import { JobCardsWerker } from "../components/JobCardsWerker";
import { JobCardsScheduler } from "../components/JobCardsScheduler";
import { PastJobFilters, DEFAULT_PAST_FILTERS } from "../utils/jobCardFilters";
import { fx_FB_SendNotification } from "../functions/Fx_SendPushNotification";
import { isDesktop } from "../utils/platformUtils";
import HomeDesktop from "./desktop/HomeDesktop";
import { useNotificationTapHandlers } from "../hooks/useNotificationTapHandlers";
import ShareInviteModal from "../components/ShareInviteModal";
import CalendarViewMobile from "../components/CalendarViewMobile";
import { schedJobsToEvents, werkerJobsToEvents, CalendarJobResource } from "../utils/calendarUtils";

type PaymentFilter = "all" | "paid" | "unpaid";
type StatusFilter = "all" | "cancelled" | "not_cancelled";
type SortMode = "smart" | "date_newest" | "date_oldest";

// -----------------------------
// Hoisted subcomponents (Option B)
// -----------------------------

const NoJobsArray: React.FC = () => {
  return (
    <React.Fragment>
      <IonRow>
        <IonCol></IonCol>
      </IonRow>
      <IonRow>
        <IonCol></IonCol>
        <IonCol size="8">
          Loading Screen
        </IonCol>
        <IonCol></IonCol>
      </IonRow>
    </React.Fragment>
  );
};

type WerkerSegmentProps = {
  colorTheme: string;
  mainSegmentWerker: string;
  onChange: (value: string) => void;
  badgeAvailable: number;
  badgeScheduled: number;
  badgePast: number;
};

const WerkerSegment: React.FC<WerkerSegmentProps> = memo(
  ({ colorTheme, mainSegmentWerker, onChange, badgeAvailable, badgeScheduled, badgePast }) => {
    return (
      <React.Fragment>
        <IonSegment
          className={colorTheme}
          value={mainSegmentWerker}
          onIonChange={(e: any) => onChange(e.detail.value!)}
        >
          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.WerkerInvited}
          >
            <IonLabel>
              {badgeAvailable > 0 ? (
                <h2>
                  Available{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgeAvailable}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Available</h2>
              )}
            </IonLabel>
          </IonSegmentButton>

          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.WerkerScheduled}
          >
            <IonLabel>
              {badgeScheduled > 0 ? (
                <h2>
                  Scheduled{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgeScheduled}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Scheduled</h2>
              )}
            </IonLabel>
          </IonSegmentButton>

          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.WerkerPast}
          >
            <IonLabel>
              {badgePast > 0 ? (
                <h2>
                  Past{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgePast}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Past</h2>
              )}
            </IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </React.Fragment>
    );
  }
);

type SchedulerSegmentProps = {
  colorTheme: string;
  mainSegmentScheduler: string;
  onChange: (value: string) => void;
  badgeOpen: number;
  badgeFilled: number;
  badgePast: number;
};

const SchedulerSegment: React.FC<SchedulerSegmentProps> = memo(
  ({ colorTheme, mainSegmentScheduler, onChange, badgeOpen, badgeFilled, badgePast }) => {
    return (
      <React.Fragment>
        <IonSegment
          className={colorTheme}
          value={mainSegmentScheduler}
          onIonChange={(e: any) => onChange(e.detail.value!)}
        >
          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.SchedOpen}
          >
            <IonLabel>
              {badgeOpen > 0 ? (
                <h2>
                  Open{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgeOpen}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Open</h2>
              )}
            </IonLabel>
          </IonSegmentButton>

          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.SchedFilled}
          >
            <IonLabel>
              {badgeFilled > 0 ? (
                <h2>
                  Filled{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgeFilled}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Filled</h2>
              )}
            </IonLabel>
          </IonSegmentButton>

          <IonSegmentButton
            value={WERK_SEGMENT_SELECTOR.SchedPast}
          >
            <IonLabel>
              {badgePast > 0 ? (
                <h2>
                  Past{" "}
                  <IonBadge
                    color="danger"
                  >
                    {badgePast}
                  </IonBadge>
                </h2>
              ) : (
                <h2>Past</h2>
              )}
            </IonLabel>
          </IonSegmentButton>
        </IonSegment>
      </React.Fragment>
    );
  }
);

type SegmentSelectorProps = {
  isScheduler: boolean;
  // werker
  werkerProps: WerkerSegmentProps;
  // scheduler
  schedulerProps: SchedulerSegmentProps;
};

const SegmentSelector: React.FC<SegmentSelectorProps> = memo(({ isScheduler, werkerProps, schedulerProps }) => {
  if (isScheduler) return <SchedulerSegment {...schedulerProps} />;
  return <WerkerSegment {...werkerProps} />;
});

type DynamicFabButtonProps = {
  isScheduler: boolean;
  fabSide: "start" | "top";
  onRealert: () => void;
  onCreateJob: () => void;
  onPayments: () => void;
  onCalendar: () => void;
  onShareInvite: () => void;
};

const DynamicFabButton: React.FC<DynamicFabButtonProps> = memo(({ isScheduler, fabSide, onRealert, onCreateJob, onPayments, onCalendar, onShareInvite }) => {
  return (
    <React.Fragment>
      <IonFab
        slot="fixed"
        style={{ marginBottom: "40px" }}
        horizontal="end"
        vertical="bottom"
      >
        {isScheduler === true && (
          <>
            <IonFabButton
              color="secondwarning"
              style={{
                // "--background": "transparent",
                // "--background-activated": "rgba(255,255,255,0.08)",
                // "--background-hover": "rgba(255,255,255,0.06)",
                // "--color": "var(--ion-color-medium-contrast)",
                "--border-radius": "15px",
                "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
              }}
            >
              <IonIcon
                // color="dark"
                icon={addOutline}
              />
            </IonFabButton>

            <IonFabList
              side="top"
            >
              <IonFabButton
                onClick={onPayments}
                color="medium"
                style={{
                  "--border-radius": "15px",
                  "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
                }}
              >
                <IonIcon
                  size="large"
                  icon={cashOutline}
                />
              </IonFabButton>
              
              <IonFabButton
                onClick={onCalendar}
                color="medium"
                style={{
                  "--border-radius": "15px",
                  "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
                }}
              >
                <IonIcon
                  size="large"
                  icon={calendarOutline}
                />
              </IonFabButton>
              
              <IonFabButton
                onClick={onRealert}
                color="medium"
                style={{
                  "--border-radius": "15px",
                  "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
                }}
              >
                <IonIcon
                  size="large"
                  icon={megaphoneOutline}
                />
              </IonFabButton>

              <IonFabButton
                onClick={onShareInvite}
                color="medium"
                style={{
                  "--border-radius": "15px",
                  "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
                }}
              >
                <IonIcon
                  size="large"
                  icon={shareSocialOutline}
                />
              </IonFabButton>

              <IonFabButton
                onClick={onCreateJob}
                color="secondwarning"
                style={{
                  "--border-radius": "15px",
                  "--box-shadow": "0 10px 26px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.10)",
                }}
              >
                <IonIcon
                  size="large"
                  icon={add}
                />
              </IonFabButton>
            </IonFabList>
          </>
        )}
      </IonFab>
    </React.Fragment>
  );
});

type MainSegmentActionsProps = {
  userLastRoleScheduler: boolean | "" | undefined;
  searchText: string;
  mainSegmentWerker: string;
  mainSegmentScheduler: string;
  paidStatus: any;
  viewOptions: { payment: PaymentFilter; status: StatusFilter; sort: SortMode };
  pastFilters?: PastJobFilters;
  onBeforeNavigate: () => Promise<void>;
};

const MainSegmentActions: React.FC<MainSegmentActionsProps> = memo(
  ({
    userLastRoleScheduler,
    searchText,
    mainSegmentWerker,
    mainSegmentScheduler,
    paidStatus,
    viewOptions,
    pastFilters,
    onBeforeNavigate,
  }) => {
    if (userLastRoleScheduler === "" || userLastRoleScheduler === undefined || userLastRoleScheduler === false) {
      return (
        <JobCardsWerker
          jobCardSearchText={searchText}
          segmentSelection={mainSegmentWerker}
          PaidStatus={paidStatus}
          viewOptions={viewOptions}
          onBeforeNavigate={onBeforeNavigate}
        />
      );
    }

    if (userLastRoleScheduler === true) {
      return (
        <JobCardsScheduler
          jobCardSearchText={searchText}
          segmentSelection={mainSegmentScheduler}
          viewOptions={viewOptions}
          pastFilters={pastFilters}
          onBeforeNavigate={onBeforeNavigate}
        />
      );
    }

    return <NoJobsArray />;
  }
);

// -----------------------------
// Home
// -----------------------------

const Home: React.FC = memo(() => {
  const history = useHistory();
  const { currentUser, authReady } = useContext(AuthContext);

  const { profile, colorTheme } = useContext(ProfileContext);

  const uid = profile?.UserId ?? "";

  const {
    navigateToWerkerShift,
    handleWerkerJobTap,
    handleSchedulerAddedTap,
    handleSchedulerEditedJobTap,
    handleSchedulerRemovedTap,
    handleSchedulerFilledJobTap,
  } = useNotificationTapHandlers(uid);

  // -------------------- DEBUG: Home mount/remount + render counter --------------------
  // const [debugHomeMountId] = useState(() => Math.random().toString(16).slice(2));
  // const debugHomeRenderCountRef = useRef(0);
  // debugHomeRenderCountRef.current += 1;

  const [mainSegmentWerker, setMainSegmentWerker] = useState<string>(
    () => sessionStorage.getItem("home_segment_werker") ?? WERK_SEGMENT_SELECTOR.WerkerInvited
  );
  const [mainSegmentScheduler, setMainSegmentScheduler] = useState<string>(
    () => sessionStorage.getItem("home_segment_scheduler") ?? WERK_SEGMENT_SELECTOR.SchedOpen
  );

  const {
    badgeCountInvitedSegmentWerkerContext,
    badgeCountScheduledSegmentWerkerContext,
    badgeCountPastSegmentWerkerContext,
    werkerJob,
    invitedFeedJobs,
  } = useContext(WerkerJobContext);

  const {
    badgeCountOpenSegmentSchedulerContext,
    badgeCountFilledSegmentSchedulerContext,
    badgeCountPastSegmentSchedulerContext,
    schedJob,
  } = useContext(SchedJobContext);

  const [present] = useIonAlert();
  const [presentToast] = useIonToast();
  const [toastIsOpen, setToastIsOpen] = useState(false);
  const [showShareInviteModal, setShowShareInviteModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  // Werker onboarding slides
  const [showWerkerOnboarding, setShowWerkerOnboarding] = useState(false);
  const werkerOnboardingChecked = useRef(false);
  useEffect(() => {
    // Suppress while CreateAccount is still running its signup writes;
    // otherwise this fires as a portal on top of /createAccount.
    if (sessionStorage.getItem('signupInProgress') === '1') return;
    if (!werkerOnboardingChecked.current && profile.UserId && !profile.OnboardingWerkerSeen) {
      werkerOnboardingChecked.current = true;
      setShowWerkerOnboarding(true);
    }
  }, [profile.UserId, profile.OnboardingWerkerSeen]);

  // Scheduler onboarding slides — owned by Home so the modal lives in one place
  // tied to Home's lifecycle (avoids per-page WerkHeader duplication / flash).
  const [showSchedulerOnboarding, setShowSchedulerOnboarding] = useState(false);
  const schedulerOnboardingChecked = useRef(false);
  useEffect(() => {
    if (
      !schedulerOnboardingChecked.current &&
      profile.UserId &&
      profile.UserLastRoleScheduler === true &&
      profile.OnboardingWerkerSeen === true &&
      !profile.OnboardingSchedulerSeen
    ) {
      schedulerOnboardingChecked.current = true;
      setShowSchedulerOnboarding(true);
    }
  }, [
    profile.UserId,
    profile.UserLastRoleScheduler,
    profile.OnboardingWerkerSeen,
    profile.OnboardingSchedulerSeen,
  ]);

  const [paidStatus] = useState<any>("");

  // ----------------------------- Scroll Restore (per segment) -----------------------------
  const contentRef = useRef<HTMLIonContentElement | null>(null);
  const listScrollTopBeforeNavRef = useRef<number | null>(null);

  const saveScrollBeforeNav = async () => {
    const el = await contentRef.current?.getScrollElement();
    if (!el) return;
    listScrollTopBeforeNavRef.current = el.scrollTop ?? 0;
  };

  // ----------------------------- View Options -----------------------------
  const sortSelectRef = useRef<HTMLIonSelectElement | null>(null);

  const openSortPopover = async (ev?: UIEvent) => {
    // Opens the IonSelect popover directly (no “extra selector click”)
    await sortSelectRef.current?.open(ev);
  };

  const [viewOptions, setViewOptions] = useState<{
    payment: PaymentFilter;
    status: StatusFilter;
    sort: SortMode;
  }>({
    payment: "unpaid",
    status: "not_cancelled",
    sort: "smart",
  });

  const _clearOptions = () => {
    setViewOptions({
      payment: "unpaid",
      status: "not_cancelled",
      sort: "smart",
    });
  };

  const [schedPastFilters, setSchedPastFilters] = useState<PastJobFilters>(DEFAULT_PAST_FILTERS);
  const [draftPastFilters, setDraftPastFilters] = useState<PastJobFilters>(DEFAULT_PAST_FILTERS);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  const hasActiveFilter =
    schedPastFilters.werkersPaid !== DEFAULT_PAST_FILTERS.werkersPaid ||
    schedPastFilters.werkersUnpaid !== DEFAULT_PAST_FILTERS.werkersUnpaid ||
    schedPastFilters.jobPaid !== DEFAULT_PAST_FILTERS.jobPaid ||
    schedPastFilters.jobUnpaid !== DEFAULT_PAST_FILTERS.jobUnpaid ||
    schedPastFilters.jobCancelled !== DEFAULT_PAST_FILTERS.jobCancelled;

  const hasActiveSort = viewOptions.sort !== "smart";

  const _hasActiveChips = viewOptions.payment !== "unpaid" || viewOptions.status !== "not_cancelled";

  const isScheduler = profile.UserLastRoleScheduler === true;
  const isSchedulerPast = isScheduler && mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedPast;
  const isWerker = profile.UserLastRoleScheduler !== true;
  const isWerkerPast = isWerker && mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerPast;

  const [viewMode, setViewMode] = useState<"list" | "calendar">("list");

  const canUseCalendar = isScheduler
    ? mainSegmentScheduler !== WERK_SEGMENT_SELECTOR.SchedOpen
    : mainSegmentWerker !== WERK_SEGMENT_SELECTOR.WerkerInvited;

  // Auto-reset to list when switching to a segment that doesn't support calendar
  useEffect(() => {
    if (!canUseCalendar && viewMode === "calendar") {
      setViewMode("list");
    }
  }, [canUseCalendar, viewMode]);

  const calendarEvents = useMemo(() => {
    let all = isScheduler
      ? schedJobsToEvents(schedJob ?? [])
      : werkerJobsToEvents([...(invitedFeedJobs ?? []), ...(werkerJob ?? [])]);

    return all.filter((ev) => {
      if (isScheduler) {
        const s = ev.resource.jobStatusSched;
        if (mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedOpen)   return s === WORKFLOW_SCHEDULER_TYPES.Open;
        if (mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedFilled) return s === WORKFLOW_SCHEDULER_TYPES.Filled;
        if (mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedPast)   return s === WORKFLOW_SCHEDULER_TYPES.Past;
      } else {
        const s = ev.resource.jobStatusWerker;
        if (mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerInvited)   return s === WORKFLOW_WERKER_TYPES.Invited;
        if (mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerScheduled) return s === WORKFLOW_WERKER_TYPES.Scheduled;
        if (mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerPast)      return s === WORKFLOW_WERKER_TYPES.Past;
      }
      return true;
    });
  }, [isScheduler, schedJob, werkerJob, invitedFeedJobs, mainSegmentScheduler, mainSegmentWerker]);

  const handleCalendarJobSelect = (resource: CalendarJobResource) => {
    if (resource.role === "scheduler") {
      history.push(`/SchedShiftDetail/${resource.firestoreDocId}`);
    } else {
      history.push(`/ShiftDetails/${resource.firestoreDocId}/${resource.jobDocId}/${resource.schedUid ?? ""}`);
    }
  };

  // show chips only on Werker Past (Scheduler Past now uses the filter popover)
  const showPastChips = isWerkerPast;

  // Sort allowed only on:
  // - Scheduler: Filled + Past (NOT Open)
  // - Werker: Scheduled + Past (NOT Available)
  const canSort =
  (isScheduler &&
    (mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedOpen ||
      mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedFilled ||
      mainSegmentScheduler === WERK_SEGMENT_SELECTOR.SchedPast)) ||
  (isWerker &&
    (mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerScheduled ||
      mainSegmentWerker === WERK_SEGMENT_SELECTOR.WerkerPast));

  // ----------------------------- Purchases / Loading Setup -----------------------------
  useEffect(() => {
    if (profile.UserId === "") {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [profile]);

  // ----------------------------- Notifications Setup (cleaner deps) -----------------------------
  useEffect(() => {
    if (!profile?.UserId) return;

    let didCancel = false;

    const saveTokenToDB = async (tokenStr: string) => {
      try {
        const tokenFirst15 = tokenStr.substring(0, 15);
        const tokenRef = doc(db, "FCMTokens", profile.UserId + "_" + tokenFirst15);
        await setDoc(tokenRef, {
          userID: profile.UserId,
          FCMToken: tokenStr,
          lastUpdated: Date.now(),
        });
      } catch (err) {
        // optional
      }
    };

    const registerForPush = async () => {
      await PushNotifications.register();

      await PushNotifications.addListener("registration", (token: Token) => {
        if (didCancel) return;
        saveTokenToDB(token.value);
      });

      await PushNotifications.addListener("registrationError", (_error: any) => {
        // optional
      });

      await PushNotifications.addListener("pushNotificationReceived", (_notification: PushNotificationSchema) => {
        // optional
      });

      await PushNotifications.addListener("pushNotificationActionPerformed", (notification: ActionPerformed) => {
        if (didCancel) return;
        const data = notification.notification.data || {};
        const type = data.type as string | undefined;
        switch (type) {
          case PUSH_NOTIFICATION_TYPES.WerkerJobAvailable:
          case PUSH_NOTIFICATION_TYPES.WerkerJobReminder:
            if (data.uniqueJobDocId && data.schedUid) {
              handleWerkerJobTap(data.uniqueJobDocId, data.schedUid);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.SchedulerAddedWerkerToShift:
            if (data.werkerJobDocId && data.uniqueJobDocId && data.schedUid) {
              handleSchedulerAddedTap(data.werkerJobDocId, data.uniqueJobDocId, data.schedUid);
            } else if (data.uniqueJobDocId && data.schedUid) {
              navigateToWerkerShift(data.uniqueJobDocId, data.schedUid);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.SchedulerRemovedWerkerFromShift:
            if (data.werkerJobDocId && data.uniqueJobDocId) {
              handleSchedulerRemovedTap(data.werkerJobDocId, data.uniqueJobDocId);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.SchedulerEditedJob:
            if (data.werkerJobDocId && data.uniqueJobDocId) {
              handleSchedulerEditedJobTap(data.werkerJobDocId, data.uniqueJobDocId);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.SchedulerFilledJobs:
          case PUSH_NOTIFICATION_TYPES.SchedulerUnFilledJobs:
            if (data.uniqueJobDocId) {
              handleSchedulerFilledJobTap(data.uniqueJobDocId);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.AssociationRequest:
            if (data.fromUID) {
              history.push(`/AssociateProfile/${data.fromUID}`);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.SchedulerNewAssociateFromInvite:
            if (data.werkerUid) {
              history.push(`/AssociateProfile/${data.werkerUid}`);
            } else if (data.fromUID) {
              history.push(`/AssociateProfile/${data.fromUID}`);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.DirectMessage:
            if (data.chatId) {
              history.push(`/Chat/${data.chatId}`);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.JobMessage:
            if (data.chatId) {
              history.push(`/Chat/${data.chatId}`);
            } else {
              history.push("/notifications");
            }
            break;
          case PUSH_NOTIFICATION_TYPES.WerkerPaymentReceived:
            if (data.schedulerUid && data.receiptId) {
              history.push(`/notifications/receipt/${data.schedulerUid}/${data.receiptId}`);
            } else {
              history.push("/notifications");
            }
            break;
          default:
            history.push("/notifications");
            break;
        }
      });
    };

    const registerNotifications = async () => {
      try {
        const res = await PushNotifications.checkPermissions();
        if (res.receive !== "granted") {
          const req = await PushNotifications.requestPermissions();
          if (req.receive !== "granted") return;
        }
        await registerForPush();
      } catch (err) {
        // console.log(err);
      }
    };

    registerNotifications();

    return () => {
      didCancel = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.UserId]);

  // ----------------------------- Navigation -----------------------------
  const navigateToCreateJobPage = () => {
    history.push({
      pathname: "/ScheduleJob",
      state: { from: "Home" },
    });
  };

  const navigateToCreateJobWithPaywall = () => {
    if (profile.SubscriptionType !== "Scheduler") {
      history.push({
        pathname: "/Paywall",
        state: { cameFrom: "Home.CreateJob" },
      });
      return;
    }
    navigateToCreateJobPage();
  };

  const handleSchedShiftReminderAllJobs = () => {
    const sendReminderToWerkers = async () => {
      try {
        const uniqueJobIds = new Set<string>();
        const unInviteRef = query(
          collectionGroup(db, FB_COLLECTION_PATH.JobsDocs),
          where("docStamp.userInfoFrom.uid", "==", profile.UserId),
          where("jobInfo.status.jobStatusWerker", "==", WORKFLOW_WERKER_TYPES.Invited)
        );

        const querySnapshot = await getDocs(unInviteRef);

        // Build a map of werkerUID -> first uniqueJobDocId so each werker
        // gets a notification that deep-links to their specific job.
        const werkerJobMap = new Map<string, string>();

        // Update isRead and collect data in one pass
        await Promise.all(
          querySnapshot.docs.map(async (docSnap) => {
            const werkerJobDoc = docSnap.data();
            const werkerUid: string = werkerJobDoc.docStamp?.userInfoTo?.uid ?? "";
            const uniqueJobDocId: string = werkerJobDoc.jobInfo?.core?.uniqueJobId ?? "";

            if (werkerUid && profile.UserId !== werkerUid) {
              // Keep the first job found per werker for the notification payload
              if (!werkerJobMap.has(werkerUid) && uniqueJobDocId) {
                werkerJobMap.set(werkerUid, uniqueJobDocId);
              }
            }

            if (uniqueJobDocId) {
              uniqueJobIds.add(uniqueJobDocId);
            }

            await updateDoc(docSnap.ref, { "jobInfo.status.isRead": false });
          })
        );

        // Send one notification per werker with their specific job ID
        await Promise.all(
          Array.from(werkerJobMap.entries()).map(([werkerUid, uniqueJobDocId]) =>
            fx_FB_SendNotification(profile.UserId, [werkerUid], PUSH_NOTIFICATION_TYPES.WerkerJobReminder, {
              schedFName: profile.FirstName,
              schedUid: profile.UserId,
              jobPay: "",
              uniqueJobDocId: uniqueJobDocId,
            })
          )
        );

        // Show completion toast immediately after operation completes
        console.log("Re-alert complete for", uniqueJobIds.size, "jobs");
        await presentToast({
          message: `Re-alert complete for ${uniqueJobIds.size} job${uniqueJobIds.size !== 1 ? 's' : ''}`,
          duration: 3000,
          position: "top",
          color: "success",
        }).then(() => console.log("Completion toast presented successfully"))
          .catch(err => console.error("Completion toast error:", err));
      } catch (error) {
        console.error("sendReminderToWerkers error:", error);
        // Show error toast
        await presentToast({
          message: "Re-alert failed, please try again",
          duration: 2500,
          position: "top",
          color: "danger",
        }).catch(err => console.error("Error toast error:", err));
      }
    };

    present({
      header: "Re-alert Invited Werkers On All Jobs?",
      buttons: [
        "Cancel",
        {
          text: "Ok",
          handler: () => {
            sendReminderToWerkers();
            return true;
          },
        },
      ],
    });
  };

  const handleCreateJob = async () => {
    if (profile.Associates.length === 1 && profile.Associates[0] === "XXX") {
      present({
        header: "You have no associates",
        buttons: [{ text: "Create Job Anyway?", handler: navigateToCreateJobWithPaywall }, "Cancel"],
      });
    } else {
      present({
        header: "Add new job",
        buttons: ["Cancel", { text: "Ok", handler: navigateToCreateJobWithPaywall }],
      });
    }
  };

  const handlePayments = () => {
    history.push('/payments');
  };

  const handleSegmentSelection = async (selection: any) => {
    sessionStorage.setItem("home_segment_werker", selection);
    setMainSegmentWerker(selection);
  };

  const handleSegmentSelectionSched = async (selection: any) => {
    sessionStorage.setItem("home_segment_scheduler", selection);
    setMainSegmentScheduler(selection);
  };

  const searchTextFunction = (typedValue: string) => {
    setSearchText(typedValue);
  };

  if (!authReady) {
    return <LoadingScreen LoadingText="Loading" />;
  }

  if (currentUser.uid === "") {
    return <Redirect exact from="/" to="login" />;
  }

  if (isDesktop()) {
    return <HomeDesktop />;
  } else
    return (
      <IonPage
        className={isLoading === true ? "bckgrnd" : ""}
      >
        {isLoading === true && <LoadingScreen LoadingText={"Loading"} />}

        {isLoading === false && (
          <>
            {/* Pass segment + searchbar + chips as `subheader` so they render
                INSIDE <IonHeader> and slide with the page on iOS. Bare <div>
                siblings between IonHeader and IonContent get left behind by
                Ionic's iOS slide transition. */}
            <WerkerSchedulerHeader
              title={HEADERTEXT_TYPES.Home}
              subheader={<>
            <div style={{ height: "12px" }} />

            {/* DEBUG banner (remove after testing) */}
            {/* <div
              style={{
                position: "sticky",
                top: 0,
                zIndex: 9999,
                background: "rgba(0,0,0,0.65)",
                color: "white",
                padding: "6px 10px",
                fontSize: "12px",
              }}
            >
              <div>Home mountId: {debugHomeMountId}</div>
              <div>Home renders: {debugHomeRenderCountRef.current}</div>
              <div>Feed snapshots: {debugFeedSnapCount}</div>
              <div>Legacy snapshots: {debugLegacySnapCount}</div>
            </div> */}

            <SegmentSelector
              isScheduler={profile.UserLastRoleScheduler === true}
              werkerProps={{
                colorTheme,
                mainSegmentWerker,
                onChange: (v) => handleSegmentSelection(v),
                badgeAvailable: badgeCountInvitedSegmentWerkerContext,
                badgeScheduled: badgeCountScheduledSegmentWerkerContext,
                badgePast: badgeCountPastSegmentWerkerContext,
              }}
              schedulerProps={{
                colorTheme,
                mainSegmentScheduler,
                onChange: (v) => handleSegmentSelectionSched(v),
                badgeOpen: badgeCountOpenSegmentSchedulerContext,
                badgeFilled: badgeCountFilledSegmentSchedulerContext,
                badgePast: badgeCountPastSegmentSchedulerContext,
              }}
            />

            {/* Searchbar row */}
            <div style={{ display: "flex", alignItems: "center" }}>
              <IonSearchbar
                value={searchText}
                onIonChange={(e) => searchTextFunction(e.detail.value!)}
                animated
                style={{ flex: 1 }}
              />
            </div>

            {/* View toggle (left) + Filter/Sort (right) */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "0 4px",
                minHeight: 36,
              }}
            >
              {/* Left: list / calendar toggle */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <IonButton
                  fill="clear"
                  size="small"
                  color={viewMode === "list" ? "dark" : "medium"}
                  onClick={() => setViewMode("list")}
                  style={{ margin: 0 }}
                >
                  <IonIcon slot="icon-only" icon={listOutline} />
                </IonButton>
                {canUseCalendar && (
                  <IonButton
                    fill="clear"
                    size="small"
                    color={viewMode === "calendar" ? "dark" : "medium"}
                    onClick={() => setViewMode("calendar")}
                    style={{ margin: 0 }}
                  >
                    <IonIcon slot="icon-only" icon={calendarOutline} />
                  </IonButton>
                )}
              </div>

              {/* Right: filter + sort (list mode only) */}
              <div style={{ display: "flex", alignItems: "center" }}>

              {/* Filter button — Scheduler Past only */}
              {viewMode === "list" && isSchedulerPast && (
                <>
                  <IonButton
                    id="sched-past-filter-trigger"
                    fill="clear"
                    style={{ fontWeight: 600, position: "relative" }}
                    size="small"
                    onClick={() => {
                      setDraftPastFilters(schedPastFilters);
                      setFilterPopoverOpen(true);
                    }}
                  >
                    Filter
                    <IonIcon size="small" icon={filterOutline} />
                    {hasActiveFilter && (
                      <IonBadge
                        color="danger"
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "6px",
                          borderRadius: "999px",
                          width: "10px",
                          height: "10px",
                          padding: "0",
                          minWidth: "10px",
                        }}
                      >
                        {" "}
                      </IonBadge>
                    )}
                  </IonButton>

                  <IonPopover
                    trigger="sched-past-filter-trigger"
                    triggerAction="click"
                    isOpen={filterPopoverOpen}
                    onDidDismiss={() => setFilterPopoverOpen(false)}
                    style={{ "--min-width": "290px" }}
                  >
                    <IonList lines="full" style={{ paddingTop: 8, minWidth: 200 }}>
                      <IonItem>
                        <IonCheckbox
                          slot="start"
                          checked={draftPastFilters.werkersPaid}
                          onIonChange={(e) =>
                            setDraftPastFilters((p) => ({ ...p, werkersPaid: e.detail.checked }))
                          }
                        />
                        <IonLabel style={{ whiteSpace: "nowrap" }}>Werkers Paid</IonLabel>
                      </IonItem>
                      <IonItem>
                        <IonCheckbox
                          slot="start"
                          checked={draftPastFilters.werkersUnpaid}
                          onIonChange={(e) =>
                            setDraftPastFilters((p) => ({ ...p, werkersUnpaid: e.detail.checked }))
                          }
                        />
                        <IonLabel style={{ whiteSpace: "nowrap" }}>Werkers Unpaid</IonLabel>
                      </IonItem>
                      <IonItem>
                        <IonCheckbox
                          slot="start"
                          checked={draftPastFilters.jobPaid}
                          onIonChange={(e) =>
                            setDraftPastFilters((p) => ({ ...p, jobPaid: e.detail.checked }))
                          }
                        />
                        <IonLabel style={{ whiteSpace: "nowrap" }}>Job Paid</IonLabel>
                      </IonItem>
                      <IonItem>
                        <IonCheckbox
                          slot="start"
                          checked={draftPastFilters.jobUnpaid}
                          onIonChange={(e) =>
                            setDraftPastFilters((p) => ({ ...p, jobUnpaid: e.detail.checked }))
                          }
                        />
                        <IonLabel style={{ whiteSpace: "nowrap" }}>Job Unpaid</IonLabel>
                      </IonItem>
                      <IonItem>
                        <IonCheckbox
                          slot="start"
                          checked={draftPastFilters.jobCancelled}
                          onIonChange={(e) =>
                            setDraftPastFilters((p) => ({ ...p, jobCancelled: e.detail.checked }))
                          }
                        />
                        <IonLabel style={{ whiteSpace: "nowrap" }}>Job Cancelled</IonLabel>
                      </IonItem>
                    </IonList>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 8,
                        padding: "8px 12px",
                      }}
                    >
                      <IonButton
                        fill="clear"
                        size="small"
                        color="medium"
                        onClick={() => setDraftPastFilters(DEFAULT_PAST_FILTERS)}
                      >
                        Default
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        color="medium"
                        onClick={() => setFilterPopoverOpen(false)}
                      >
                        Cancel
                      </IonButton>
                      <IonButton
                        fill="solid"
                        size="small"
                        color="dark"
                        onClick={() => {
                          setSchedPastFilters(draftPastFilters);
                          setFilterPopoverOpen(false);
                        }}
                      >
                        Apply
                      </IonButton>
                    </div>
                  </IonPopover>
                </>
              )}

              {/* Sort button */}
              {viewMode === "list" && canSort && (
                <>
                  <IonButton
                    fill="clear"
                    style={{ fontWeight: 600, position: "relative" }}
                    size="small"
                    onClick={(e) => openSortPopover(e.nativeEvent as UIEvent)}
                  >
                    Sort
                    <IonIcon size="small" icon={swapVerticalOutline} />
                    {hasActiveSort && (
                      <IonBadge
                        color="danger"
                        style={{
                          position: "absolute",
                          top: "4px",
                          right: "6px",
                          borderRadius: "999px",
                          width: "10px",
                          height: "10px",
                          padding: "0",
                          minWidth: "10px",
                        }}
                      >
                        {" "}
                      </IonBadge>
                    )}
                  </IonButton>

                  {/* Hidden select (sort popover) */}
                  <IonSelect
                    ref={sortSelectRef}
                    value={viewOptions.sort}
                    interface="popover"
                    onIonChange={(e: any) => setViewOptions((p) => ({ ...p, sort: e.detail.value }))}
                    style={{ position: "absolute", opacity: 0, pointerEvents: "none", width: 1, height: 1 }}
                  >
                    <IonSelectOption value="smart">Default</IonSelectOption>
                    <IonSelectOption value="date_newest">Newest</IonSelectOption>
                    <IonSelectOption value="date_oldest">Oldest</IonSelectOption>
                  </IonSelect>
                </>
              )}

              </div>
            </div>

            {/* Chips row — Werker Past only */}
            {showPastChips && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 12px",
                  height: 30,
                  boxSizing: "border-box",
                  marginTop: "0px",
                  marginBottom: "5px",
                  overflowX: "auto",
                  overflowY: "hidden",
                  whiteSpace: "nowrap",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                <IonChip
                  className="homeChip"
                  outline={true}
                  color={
                    viewOptions.payment === "paid" || viewOptions.payment === "all" ? "dark" : "medium"
                  }
                  onClick={() => {
                    setViewOptions((p) => {
                      const paidChecked = p.payment === "paid" || p.payment === "all";
                      const nextPaidChecked = !paidChecked;
                      const unpaidChecked = p.payment === "unpaid" || p.payment === "all";
                      const next =
                        (nextPaidChecked && unpaidChecked) || (!nextPaidChecked && !unpaidChecked)
                          ? "all"
                          : nextPaidChecked
                          ? "paid"
                          : "unpaid";
                      return { ...p, payment: next };
                    });
                  }}
                >
                  <IonLabel className="homeChipLabel">Paid</IonLabel>
                </IonChip>

                <IonChip
                  className="homeChip"
                  outline={true}
                  color={
                    viewOptions.payment === "unpaid" || viewOptions.payment === "all" ? "dark" : "medium"
                  }
                  onClick={() => {
                    setViewOptions((p) => {
                      const unpaidChecked = p.payment === "unpaid" || p.payment === "all";
                      const nextUnpaidChecked = !unpaidChecked;
                      const paidChecked = p.payment === "paid" || p.payment === "all";
                      const next =
                        (paidChecked && nextUnpaidChecked) || (!paidChecked && !nextUnpaidChecked)
                          ? "all"
                          : paidChecked
                          ? "paid"
                          : "unpaid";
                      return { ...p, payment: next };
                    });
                  }}
                >
                  <IonLabel className="homeChipLabel">Unpaid</IonLabel>
                </IonChip>

                <IonChip
                  className="homeChip"
                  outline={true}
                  color={
                    viewOptions.status === "not_cancelled" || viewOptions.status === "all"
                      ? "dark"
                      : "medium"
                  }
                  onClick={() => {
                    setViewOptions((p) => {
                      const activeChecked = p.status === "not_cancelled" || p.status === "all";
                      const nextActiveChecked = !activeChecked;
                      const cancelledChecked = p.status === "cancelled" || p.status === "all";
                      const next =
                        (nextActiveChecked && cancelledChecked) || (!nextActiveChecked && !cancelledChecked)
                          ? "all"
                          : nextActiveChecked
                          ? "not_cancelled"
                          : "cancelled";
                      return { ...p, status: next };
                    });
                  }}
                >
                  <IonLabel className="homeChipLabel">Active</IonLabel>
                </IonChip>

                <IonChip
                  className="homeChip"
                  outline={true}
                  color={
                    viewOptions.status === "cancelled" || viewOptions.status === "all"
                      ? "dark"
                      : "medium"
                  }
                  onClick={() => {
                    setViewOptions((p) => {
                      const cancelledChecked = p.status === "cancelled" || p.status === "all";
                      const nextCancelledChecked = !cancelledChecked;
                      const activeChecked = p.status === "not_cancelled" || p.status === "all";
                      const next =
                        (activeChecked && nextCancelledChecked) || (!activeChecked && !nextCancelledChecked)
                          ? "all"
                          : activeChecked
                          ? "not_cancelled"
                          : "cancelled";
                      return { ...p, status: next };
                    });
                  }}
                >
                  <IonLabel className="homeChipLabel">Cancelled</IonLabel>
                </IonChip>
              </div>
            )}

            </>}
            />

            <IonContent
              ref={contentRef}
              scrollX={false}
              scrollY={viewMode === "list"}
              scrollEvents={false}
            >
              {viewMode === "calendar" ? (
                <CalendarViewMobile
                  events={calendarEvents}
                  onSelectJob={handleCalendarJobSelect}
                  isScheduler={isScheduler}
                />
              ) : (
              <MainSegmentActions
                userLastRoleScheduler={profile.UserLastRoleScheduler}
                searchText={searchText}
                mainSegmentWerker={mainSegmentWerker}
                mainSegmentScheduler={mainSegmentScheduler}
                paidStatus={paidStatus}
                viewOptions={viewOptions}
                pastFilters={isSchedulerPast ? schedPastFilters : undefined}
                onBeforeNavigate={saveScrollBeforeNav}
              />
              )}

              <DynamicFabButton
                isScheduler={profile.UserLastRoleScheduler === true}
                fabSide={
                  profile.SubscriptionType === "Werker" ||
                  profile.SubscriptionType === "NONE" ||
                  profile.SubscriptionType === ""
                    ? "start"
                    : "top"
                }
                onRealert={handleSchedShiftReminderAllJobs}
                onCreateJob={handleCreateJob}
                onPayments={handlePayments}
                onCalendar={() => history.push('/SchedulerCalendar')}
                onShareInvite={() => setShowShareInviteModal(true)}
              />

              <ShareInviteModal
                isOpen={showShareInviteModal}
                onDidDismiss={() => setShowShareInviteModal(false)}
              />

              <IonToast
                isOpen={toastIsOpen}
                message={`Reminder sent for open jobs`}
                onDidDismiss={(_e: any) => setToastIsOpen(false)}
                duration={2000}
                position="top"
              />
            </IonContent>

            <TabNavigator />

            <WerkerOnboarding
              isOpen={showWerkerOnboarding}
              onFinish={() => {
                setShowWerkerOnboarding(false);
                if (profile.UserId) {
                  fx_FB_UpdateOnboardingSeen(profile.UserId, "onboardingWerkerSeen");
                }
              }}
              onStartTrial={() => {
                setShowWerkerOnboarding(false);
                if (profile.UserId) {
                  fx_FB_UpdateOnboardingSeen(profile.UserId, "onboardingWerkerSeen");
                }
                // Skip Paywall for already-subscribed Schedulers.
                if (profile.SubscriptionType !== "Scheduler") {
                  history.push({
                    pathname: "/Paywall",
                    state: { cameFrom: "Home.Scheduler" },
                  });
                }
              }}
            />

            <SchedulerOnboarding
              isOpen={showSchedulerOnboarding}
              onFinish={() => {
                setShowSchedulerOnboarding(false);
                if (profile.UserId) {
                  fx_FB_UpdateOnboardingSeen(profile.UserId, "onboardingSchedulerSeen");
                }
                if (profile.SubscriptionType !== "Scheduler") {
                  history.push({
                    pathname: "/Paywall",
                    state: { cameFrom: "Home.Scheduler" },
                  });
                }
              }}
            />
          </>
        )}
      </IonPage>
    );
});

export default Home;
